import { useCallback, useEffect, useMemo, useState } from 'react';

import { DEFAULT_ITEMS_PER_PAGE, MAXIMUM_ITEMS_PER_PAGE } from '@/app/constants';

interface BaseResponseType {
  totalItemCount?: number;
}

interface UseLazyLoadParams<ResponseType, ListItem, RequestDto> {
  getItems: (dto: RequestDto) => Promise<ResponseType>;
  listDataKey: keyof ResponseType;
  limit?: number;
  filter?: RequestDto;
  onError?: (error: unknown) => void;
  initialData?: ListItem[];
  initialTotalCount?: number | null;
}

function useLazyLoad<ResponseType extends BaseResponseType, ListItem, RequestDto>({
  getItems,
  listDataKey,
  filter = {} as RequestDto,
  limit = DEFAULT_ITEMS_PER_PAGE,
  initialData = [],
  initialTotalCount = null,
  onError,
}: UseLazyLoadParams<ResponseType, ListItem, RequestDto>) {
  const [isLoading, setIsLoading] = useState(false);
  const [listData, setListData] = useState<ListItem[]>(initialData);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState<number | null>(initialTotalCount);

  const isLoadMoreAvailable = useMemo(() => {
    if (isLoading) {
      return false;
    }

    if (typeof totalCount !== 'number') {
      return false;
    }

    return totalCount > offset;
  }, [offset, totalCount, isLoading]);

  const handleLoadMore = useCallback(async () => {
    try {
      setIsLoading(true);

      const limit = Math.min(offset * 2, MAXIMUM_ITEMS_PER_PAGE);

      const response = await getItems({
        ...filter,
        limit,
        offset,
      });

      const nextData = response[listDataKey] as ListItem[];

      if (!nextData) {
        return null;
      }

      setOffset(offset + limit);
      setTotalCount(response.totalItemCount ?? null);

      return setListData((currentData) => [...currentData, ...nextData]);
    } catch (error) {
      onError?.(error);
    } finally {
      setIsLoading(false);
    }
  }, [filter, setOffset, offset, onError]);

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true);

        const response = await getItems({
          ...filter,
          limit,
          offset: 0,
        });

        const nextData = response[listDataKey] as ListItem[];

        if (!nextData) {
          return null;
        }

        setOffset(limit);
        setTotalCount(response.totalItemCount ?? null);

        return setListData(nextData);
      } catch (error) {
        onError?.(error);
      } finally {
        setIsLoading(false);
      }
    };

    getData();
  }, []);

  return {
    isLoading,
    isLoadMoreAvailable,
    totalCount,
    offset,
    listData,
    onLoadMore: handleLoadMore,
  };
}

export default useLazyLoad;
